import React, { useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Skeleton,
  useTheme,
} from "@mui/material";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import { AlertView } from "../../../../../models/Alerts/Alert";
import { alertsStore } from "../../../stores/alerts/AlertsStore";
import AlertRow from "./AlertRow";
import { SectionChips } from "./SectionChips";
import { alertDetailPath, useOpenAlert } from "./useOpenAlert";
import { getAlertColors } from "./alertUi";
import {
  AlertsTrackExtra,
  trackAlertsAction,
} from "../../../../../analytics/alertsTracking";

const PANEL_VIEWS: { view: AlertView; label: string }[] = [
  { view: "INBOX", label: "All" },
  { view: "UNREAD", label: "Unread" },
  { view: "OPEN", label: "Open" },
];

interface AlertsPanelProps {
  onClose: () => void;
}

const AlertsPanel: React.FC<AlertsPanelProps> = ({ onClose }) => {
  const theme = useTheme();
  const colors = getAlertColors(theme);
  const navigate = useNavigate();
  const list = alertsStore.panel;
  const openAlert = useOpenAlert("bell", list);
  const filterContext = (): AlertsTrackExtra => ({
    view: list.view,
    section: list.section,
  });
  const [isMarkingAll, setIsMarkingAll] = React.useState(false);

  useEffect(() => {
    list.setActionFeedback("");
    list.load();
    alertsStore.loadMeta();
  }, [list]);

  const totalUnread = alertsStore.totalUnread;
  const totalHigh = alertsStore.totalHighUnread;
  const sectionUnread = list.section
    ? (alertsStore.sections.find((row) => row.code === list.section)?.unread ??
      0)
    : totalUnread;

  const handleMarkAllRead = async () => {
    trackAlertsAction("mark_all_read", "bell", filterContext());
    setIsMarkingAll(true);
    list.setActionFeedback("");
    try {
      await alertsStore.markAllRead(list.section);
    } catch (error: any) {
      list.setActionFeedback(error?.message || "Failed to mark all read.");
    } finally {
      setIsMarkingAll(false);
    }
  };

  const runRowAction = async (
    action: () => Promise<void>,
    fallback: string,
  ) => {
    list.setActionFeedback("");
    try {
      await action();
    } catch (error: any) {
      list.setActionFeedback(error?.message || fallback);
    }
  };

  const handleViewAll = () => {
    trackAlertsAction("view_all", "bell", filterContext());
    const query = new URLSearchParams();
    if (list.view !== "INBOX") query.set("view", list.view);
    if (list.section) query.set("section", list.section);
    const search = query.toString();
    onClose();
    navigate(search ? `/alerts?${search}` : "/alerts");
  };

  const subtitle =
    totalUnread === 0
      ? "You're all caught up"
      : `${totalUnread} unread${totalHigh > 0 ? ` · ${totalHigh} high priority` : ""}`;

  return (
    <Box
      sx={{
        width: { xs: "calc(100vw - 24px)", sm: 440 },
        maxWidth: 440,
        display: "flex",
        flexDirection: "column",
        maxHeight: "min(640px, calc(100vh - 80px))",
        bgcolor: colors.surface,
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2, pt: 2, pb: 1.25 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box>
            <CustomTypography variant="heading5" color={colors.title}>
              Alerts
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={
                totalHigh > 0
                  ? theme.palette.customColors.errorRed
                  : colors.subtitle
              }
              fontWeight={totalHigh > 0 ? 600 : undefined}
              sx={{ mt: 0.4 }}
            >
              {subtitle}
            </CustomTypography>
          </Box>
          <Button
            size="small"
            onClick={handleMarkAllRead}
            disabled={isMarkingAll || sectionUnread === 0}
            startIcon={
              isMarkingAll ? (
                <CircularProgress size={12} sx={{ color: "inherit" }} />
              ) : (
                <DoneAllOutlinedIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 600,
              borderRadius: "8px",
              color: colors.accent,
              px: 1.25,
              "&:hover": { bgcolor: colors.hover },
            }}
          >
            {list.section ? "Mark section read" : "Mark all read"}
          </Button>
        </Box>

        {/* View switch */}
        <Box
          sx={{
            mt: 1.5,
            p: 0.5,
            display: "flex",
            gap: 0.5,
            borderRadius: "10px",
            bgcolor: colors.surfaceMuted,
            border: `1px solid ${colors.border}`,
          }}
        >
          {PANEL_VIEWS.map(({ view, label }) => {
            const active = list.view === view;
            return (
              <Box
                key={view}
                component="button"
                type="button"
                onClick={() => {
                  if (view === list.view) return;
                  trackAlertsAction("view_change", "bell", {
                    view,
                    section: list.section,
                  });
                  list.setView(view);
                }}
                sx={{
                  flex: 1,
                  height: 30,
                  border: 0,
                  cursor: "pointer",
                  borderRadius: "8px",
                  fontFamily: "inherit",
                  fontSize: "0.78rem",
                  fontWeight: active ? 700 : 500,
                  color: active ? colors.title : colors.subtitle,
                  bgcolor: active ? colors.surface : "transparent",
                  boxShadow: active
                    ? `0 1px 3px ${theme.palette.customColors.shadowLightBorder}`
                    : "none",
                  transition: "all 0.15s ease",
                  "&:hover": { color: colors.title },
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>

        <SectionChips
          sx={{ mt: 1.25 }}
          value={list.section}
          onChange={(section) => {
            if (section === list.section) return;
            trackAlertsAction("section_change", "bell", {
              view: list.view,
              section,
            });
            list.setSection(section);
          }}
        />
      </Box>

      <Box sx={{ px: 2 }}>
        <InlineFormError message={list.actionError} sx={{ mt: 0 }} />
      </Box>

      {/* List */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          borderTop: `1px solid ${colors.hairline}`,
          minHeight: 160,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: colors.border,
            borderRadius: 4,
          },
        }}
      >
        {list.isLoading && !list.hasLoaded ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Box
              key={index}
              sx={{ display: "flex", gap: 1.25, px: 2, py: 1.5 }}
            >
              <Skeleton
                variant="rounded"
                width={34}
                height={34}
                sx={{ borderRadius: "10px" }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="80%" height={18} />
                <Skeleton width="55%" height={14} />
                <Skeleton
                  width={90}
                  height={22}
                  sx={{ borderRadius: "999px" }}
                />
              </Box>
            </Box>
          ))
        ) : list.error ? (
          <Box sx={{ p: 2 }}>
            <InlineFormError message={list.error} />
            <Button
              size="small"
              onClick={() => {
                trackAlertsAction("retry", "bell", filterContext());
                list.load();
              }}
              sx={{
                textTransform: "none",
                color: colors.accent,
                fontWeight: 600,
              }}
            >
              Try again
            </Button>
          </Box>
        ) : list.items.length === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: colors.surfaceMuted,
                border: `1px solid ${colors.border}`,
                color: colors.muted,
                mb: 1.5,
              }}
            >
              <NotificationsOffOutlinedIcon />
            </Box>
            <CustomTypography variant="heading7" color={colors.title}>
              {list.view === "UNREAD" ? "No unread alerts" : "Nothing here"}
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={colors.subtitle}
              sx={{ mt: 0.5 }}
            >
              New alerts for your sections will show up here.
            </CustomTypography>
          </Box>
        ) : (
          <Box
            sx={{
              opacity: list.isLoading ? 0.6 : 1,
              transition: "opacity 0.15s",
            }}
          >
            {list.items.map((alert) => (
              <AlertRow
                key={alert.id}
                alert={alert}
                compact
                onOpen={() => openAlert(alert, onClose)}
                onOpenDetail={() => {
                  trackAlertsAction("row_detail", "bell", {
                    ...filterContext(),
                    alert,
                  });
                  onClose();
                  navigate(alertDetailPath(alert.id));
                }}
                onMarkRead={() => {
                  trackAlertsAction("row_read", "bell", {
                    ...filterContext(),
                    alert,
                  });
                  runRowAction(
                    () => alertsStore.markRead([alert.id]),
                    "Failed to mark read.",
                  );
                }}
                onArchive={() => {
                  trackAlertsAction("row_archive", "bell", {
                    ...filterContext(),
                    alert,
                  });
                  runRowAction(
                    () => alertsStore.archive([alert.id]),
                    "Failed to archive.",
                  );
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          borderTop: `1px solid ${colors.hairline}`,
          px: 1,
          py: 0.75,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Button
          fullWidth
          onClick={handleViewAll}
          endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: "none",
            fontSize: "0.8rem",
            fontWeight: 600,
            color: colors.accent,
            borderRadius: "8px",
            "&:hover": { bgcolor: colors.hover },
          }}
        >
          {list.totalCount > list.items.length
            ? `View all ${list.totalCount} alerts`
            : "Open alerts inbox"}
        </Button>
      </Box>
    </Box>
  );
};

export default observer(AlertsPanel);
